import L from "leaflet";
import "leaflet-geometryutil";
import React, { useEffect, useState } from "react";
import { Badge, Button } from "react-bootstrap";
import { Marker, Polygon, Popup } from "react-leaflet";
import { useNavigate } from "react-router-dom";

import "./AirportMarker.css";

import { StationStatus } from "utils";
import { useMarkerStatus, useWebSocket } from "utils/hooks";
import { Back, FileBarGraph, Soundwave } from "react-bootstrap-icons";

/**
 * Creates a Leaflet icon with specified image, size and CSS class.
 * @memberof module:components/markers
 * @param {string} iconUrl - URL of the icon image.
 * @param {number[]} iconSize - Size of the icon in pixels [width, height].
 * @param {string} [className] - Optional CSS class name(s) for the icon.
 * @returns {L.Icon} Leaflet icon instance.
 */
function createIcon(iconUrl, iconSize, className) {
  return new L.icon({
    iconUrl: iconUrl,
    iconSize: iconSize,
    className: className,
  });
}

/**
 * Predefined marker icons with different colors and styles.
 * @type {Object.<string, L.Icon>}
 */
const markerIcons = {
  green: createIcon("/img/green_pin.png", [40, 40], "animated-icon"),
  yellow: createIcon("/img/yellow_pin.png", [40, 40], "animated-icon"),
  red: createIcon("/img/red_pin.png", [40, 40], "animated-icon"),
  gray: createIcon("/img/gray_pin.png", [40, 40], "animated-icon"),
  noInterference: createIcon(
    "/img/no_interference_pin.png",
    [70, 70],
    "blink-fade"
  ),
  noInterferenceData: createIcon(
    "/img/disconnected_pin.png",
    [70, 70],
    "blink-fade"
  ),
  interferenceDetected: createIcon(
    "/img/interference_detected_pin.png",
    [70, 70],
    "blink-fade"
  ),
  disconnected: createIcon("/img/disconnected_pin.png", [70, 70], "blink-fade"),
  retryConnection: createIcon("/img/retry_connection_pin.png", [70, 70]),
};

/**
 * Returns a Bootstrap Badge component representing station state based on compliance level.
 * @param {number} level - Compliance level (0 = compliant, 1 = attention required, 2 = noncompliant).
 * @returns {JSX.Element} Badge component.
 */
const getStationState = (level) => {
  switch (level) {
    case 0:
      return <Badge bg="success"> COMPLIANT</Badge>;
    case 1:
      return <Badge bg="warning"> REQUIRES ATTENTION</Badge>;
    case 2:
      return <Badge bg="danger"> NONCOMPLIANT</Badge>;
    default:
      return <Badge bg="secondary"> NO DATA AVAILABLE</Badge>;
  }
};

/**
 * React component representing a GNSS Station Marker on the map with interactive icon and popup.
 * Displays real-time status and navigation buttons to reports.
 * @memberof module:components/markers
 * @param {Object} props
 * @param {number} props.zoom - Current map zoom level.
 * @param {Object} props.details - Station details containing location, identifier, name, etc.
 * @param {Object} props.map - Leaflet map instance.
 * @param {Object} props.position - Fallback position if details is not provided.
 * @param {function(string): void} props.onStatusChanged - Callback when status changes.
 *
 * @returns {JSX.Element} Station marker element with interactive popup.
 */
const AirportMarker = ({ zoom, details, map, position, onStatusChanged }) => {
  const [onError, onSuccess, subscribe, unsubscribe] = useWebSocket();
  const { connectionStatusHistory, warning, status } = useMarkerStatus(
    details.identifier,
    null,
    onStatusChanged
  );

  const navigate = useNavigate();

  const [bearArr, setBearArr] = useState([]);
  const [isHovered, setHover] = useState(false);
  const [customMarker, setCustomMarker] = useState(markerIcons.gray);

  /**
   * Rotates a point around a base point by a given angle (degrees).
   * Uses leaflet-geometryutil's rotatePoint.
   *
   * @param {number[]} base - Base point as [lat, lng].
   * @param {number[]} point - Point to rotate as [lat, lng].
   * @param {number} angle - Angle in degrees to rotate clockwise.
   * @returns {number[]} Rotated point [lat, lng].
   */
  const rotatePoint = (base, point, angle) => {
    if (map === null) return point;
    const { lat, lng } = L.GeometryUtil.rotatePoint(
      map,
      L.latLng(point[0], point[1]),
      angle,
      L.latLng(base[0], base[1])
    );
    return [lat, lng];
  };

  /**
   * Calculates a destination point given a base point, heading, and distance.
   * Uses leaflet-geometryutil's destination method.
   *
   * @param {number[]} base - Base point [lat, lng].
   * @param {number} heading - Heading in degrees.
   * @param {number} distance - Distance in meters.
   * @returns {number[]} Destination point [lat, lng].
   */
  const destination = (base, heading, distance) => {
    if (map === null) return base;
    const { lat, lng } = L.GeometryUtil.destination(
      L.latLng(base[0], base[1]),
      heading,
      distance
    );

    return [lat, lng];
  };

  /**
   * Creates a red semi-transparent polygon indicating interference direction.
   *
   * @param {number} degree - Base heading angle in degrees.
   * @returns {JSX.Element} Leaflet Polygon element.
   */
  const getInterferenceIndicator = (degree) => {
    const offset = 60000 - 5000 * (zoom - 5);
    const base = [details.location.lat, details.location.lon];
    const rotatedPoints = [-15, -10, -5, 0, 5, 10, 15].map((delta) =>
      rotatePoint(base, destination(base, 0, offset), degree + delta)
    );

    return (
      <Polygon
        pathOptions={{ color: "red", opacity: 0.2 }}
        positions={[base, ...rotatedPoints]}
      />
    );
  };

  /**
   * Updates the current marker icon based on the station status.
   * Uses local state to trigger re-render.
   */
  const getMarkerIcon = () => {

    switch (status.code) {
      case StationStatus.state.DATA_UNAVAILABLE:
        setCustomMarker(markerIcons.yellow);
        break;
      case StationStatus.state.SPECTRUM_CLEAN:
        setCustomMarker(markerIcons.green);
        break;
      case StationStatus.state.INTERFERENCE_DETECTED:
      case StationStatus.state.SPOOFING_DETECTED:
      case StationStatus.state.SPOOFING_AND_INTERFERENCE_DETECTED:
        setCustomMarker(markerIcons.red);

        break;
      case StationStatus.state.DISCONNECTED:
      case StationStatus.state.CONNECTING:
      default:
        setCustomMarker(markerIcons.gray);
    }
  };

  /**
   * Returns a marker component representing the station state icon.
   * @returns {JSX.Element|null} Leaflet Marker or null.
   */
  const getMarkerState = () => {
    switch (status.code) {
      case StationStatus.state.DISCONNECTED:
        return (
          <Marker
            position={[details.location.lat, details.location.lon]}
            icon={markerIcons.disconnected}
          />
        );
      case StationStatus.state.DATA_UNAVAILABLE:
        return (
          <Marker
            position={[details.location.lat, details.location.lon]}
            icon={markerIcons.noInterferenceData}
          />
        );
      case StationStatus.state.SPECTRUM_CLEAN:
        return (
          <Marker
            position={[details.location.lat, details.location.lon]}
            icon={markerIcons.noInterference}
          />
        );
      case StationStatus.state.INTERFERENCE_DETECTED:
      case StationStatus.state.SPOOFING_DETECTED:
      case StationStatus.state.SPOOFING_AND_INTERFERENCE_DETECTED:
        return (
          <Marker
            position={[details.location.lat, details.location.lon]}
            icon={markerIcons.interferenceDetected}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {}, [zoom]);

  useEffect(() => {
    getMarkerIcon();
    getMarkerState();
    if (!details) return;
  }, [status.code]);

  return (
    <>
      {getMarkerState()}

      <Marker
        zIndexOffset={isHovered ? 200 : 1}
        data={details}
        position={
          details
            ? [details.location.lat, details.location.lon]
            : [position.lat, position.lon]
        }
        icon={customMarker}
        eventHandlers={{
          mouseover: (e) => {
            setHover(true);
            customMarker.options.iconSize = [70, 70];
            e.target.setIcon(customMarker);
          },
          mouseout: (e) => {
            setHover(false);
            customMarker.options.iconSize = [40, 40];
            e.target.setIcon(customMarker);
          },
        }}
      >
        {details ? (
          <Popup direction="top" offset={[0, -35]}>
            <h6 style={{ color: "#fff" }}>{details.name}</h6>
            <div className="col-md-12 mb-2" style={{ color: "#fff" }}>
              <small style={{ display: "block" }}>
                Identifier: {details.identifier}
              </small>
              <small style={{ display: "block" }}>Type: {details.type}</small>
              <small style={{ display: "block" }}>
                Longitude: {details.location.lon}
              </small>
              <small style={{ display: "block" }}>
                Latitude: {details.location.lat}
              </small>
            </div>

            <div
              className="col-md-12 mb-2"
              style={{ display: "flex", justifyContent: "center" }}
            >
              <Button
                className="d-flex align-items-center justify-content-center text-nowrap col-md-6 mb-1 me-1"
                size="sm"
                variant="outline-success"
                onClick={() => {
                  navigate("/gnss/performance-report", {
                    state: { identifier: details.identifier },
                  });
                }}
              >
                <FileBarGraph
                  width={"1.2em"}
                  height={"1.2em"}
                  className="me-1"
                ></FileBarGraph>{" "}
                Periodic report
              </Button>

              <Button
                className="d-flex align-items-center justify-content-center text-nowrap col-md-6 mb-1 ms-1"
                size="sm"
                variant="outline-success"
                onClick={() => {
                  navigate("/gnss/spectrum", {
                    state: { identifier: details.identifier },
                  });
                }}
              >
                <Soundwave
                  width={"1.2em"}
                  height={"1.2em"}
                  className="me-1"
                ></Soundwave>
                Realtime report
              </Button>
            </div>

            {/* {details && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                {getStationState(details.level)}
              </div>
            )} */}
            {status.code === StationStatus.state.INTERFERENCE_DETECTED && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <h6>
                  <Badge className="blink-fade" bg="danger">
                    {status.message}
                  </Badge>
                </h6>
              </div>
            )}
            {status.code === StationStatus.state.SPECTRUM_CLEAN && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <h6>
                  <Badge bg="success"> {status.message}</Badge>
                </h6>
              </div>
            )}
            {status.code === StationStatus.state.DATA_UNAVAILABLE && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <h6>
                  <Badge bg="warning"> {status.message}</Badge>
                </h6>
              </div>
            )}
            {status.code === StationStatus.state.DISCONNECTED && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <h6>
                  <Badge bg="secondary"> {status.message}</Badge>
                </h6>
              </div>
            )}
          </Popup>
        ) : (
          <></>
        )}
      </Marker>
    </>
  );
};

export default AirportMarker;
